/* eslint-disable react/jsx-props-no-spreading */
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { HiOutlineDuplicate } from 'react-icons/hi';
import { useEffect, useState } from 'react';
import { checkPermission, copyToClipboard, formatDateNotTime } from 'utils/utils';
import { getStockById, getStockLedgersByFilter } from 'api/stockApi';
import { defaultpageCount, permissionKeys, routePaths, sortByTypeType } from 'utils/constants';
import { useSelector } from 'react-redux';
import { orgSelector, userSelector } from 'redux/selectors';
import ButtonBack from 'components/button/buttonBack';
import { Table } from 'flowbite-react';
import { stockLedgerTypes } from 'utils/proptypes';
import ActionTable from 'components/table/actionTable';
import SpinnerComponent from 'components/spinner';
import PaginateTable from 'components/table/paginate';

const StockLedger = (props: any) => {
  const { type } = props;
  const { WRITE_STOCK, PLATFORM_ADMIN } = permissionKeys;
  const params: any = useParams();
  const navigate = useNavigate();
  const { userInfo } = useSelector(userSelector);
  const { organizationId } = useSelector(orgSelector);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(defaultpageCount);
  const [totalEntities, setTotalEntities] = useState(0);
  const [stockLedger, setStockLedger] = useState<any>(null);
  const [stock, setStock] = useState<any>(null);
  const [t] = useTranslation();
  const [sortBy, setSortBy] = useState('');
  const [sortByType, setSortByType] = useState('');
  const isWriteOrg = checkPermission(userInfo, props.type, [WRITE_STOCK], organizationId);
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const locationState = useLocation();
  const state = locationState.state;

  useEffect(() => {
    setIsLoading(true)
    getStockLedgersByFilter({ page: currentPage, limit: pageCount, sortBy, sortByType, stockId: params?.id }).then(res => {
      setData(res.data);
      setIsLoading(false);
    });
    getStockById(params?.id).then(res => {
      setStock(res.data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.id, currentPage]);

  useEffect(() => {
    if (data) {
      setStockLedger(data?.entities || []);
      setTotalEntities(data.totalEntities || 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleCopyToClipboard = (e: any, item: any, type: string) => {
    switch (type) {
      case 'amount':
        copyToClipboard(item.amount, t('stockPage.copiedAmount'));
        break;
      case 'reasonforDiscrepancy':
        copyToClipboard(item?.reasonForDiscrepancy?.name, t('stockPage.copiedReasonforDiscrepancy'));
        break;
      case 'discreprancyNotes':
        copyToClipboard(item.discreprancyNotes, t('stockPage.copiedDiscrepancyNote'));
        break;
    case 'user':
        copyToClipboard(`${item.user?.firstName} ${item.user?.lastName}`, t('stockPage.copiedUser'));
        break;
    }
    e.stopPropagation();
  };

  const handleHeaderSort = (field: string) => {
    setSortBy(field);
    let type = '';
    if (field !== sortBy) {
      type = sortByTypeType.ASC;
    } else if (sortByType === sortByTypeType.ASC) {
      type = sortByTypeType.DESC;
    } else {
      setSortBy(type);
    }
    setSortByType(type);
  };

  const handleBack = () => {
    navigate(`${routePaths.STOCK_PAGE}`, {state: {page: state.page - 1, locationId: state.locationId, sortBy: state.sortBy, sortByType: state.sortByType }});
  }

  return (
    <div className="flex flex-col user-detail-container w-full organization">
      {stockLedger && (
        <>
          <div className="p-[15px] cursor-pointer">
          <ButtonBack buttonName={t('back')} handleBack={() => handleBack()}/>
          </div>
          {/*header*/}
          <div className="user-summary w-full px-[30px] py-[15px] flex flex-col text-black">
            <p className="mr-2 text-lg">{t('stockPage.itemName')}</p>
            <p className="text-[30px] font-semibold">{stock?.item?.name}</p>
          </div>
          {/*body*/}
          <div className="w-full organization p-[30px]">

            <Table className="border-b">
              <Table.Head className="text-xs text-gray-900 border-b-2 border-gray-100">
                <Table.HeadCell className="cursor-pointer bg-white font-medium text-xs px-3">
                  <div className="flex items-center ">
                    {t('stockPage.amount')}
                  </div>
                </Table.HeadCell>
                <Table.HeadCell
                  className="hidden-mobile bg-white font-medium text-xs"
                >
                  <div className="flex items-center">
                    {t('stockPage.reasonForDiscrepency')}
                  </div>
                </Table.HeadCell>
                <Table.HeadCell
                  className="hidden-mobile bg-white font-medium text-xs"
                >
                  <div className="flex items-center">
                    {t('stockPage.discrepencyNotes')}
                  </div>
                </Table.HeadCell>
                <Table.HeadCell className="hidden-mobile bg-white font-medium text-xs">
                  <div className="flex items-center">{t('stockPage.user')}</div>
                </Table.HeadCell>
                <Table.HeadCell
                  className="hidden-mobile w-[220px] bg-white font-medium text-xs"
                >
                  <div className="flex items-center">
                    {t('stockPage.lastReconcile')}
                  </div>
                </Table.HeadCell>
              </Table.Head>
              <Table.Body>
                {stockLedger.map((item: stockLedgerTypes, index: number) => (
                  <Table.Row key={`row-${index + 1}`} className="cursor-pointer bg-white text-sm hover:bg-gray-50 border-b border-gray-100">
                    <Table.Cell className="py-[10px] px-3">
                      <div className="flex flex-row items-center">
                        <div className="text-black">{item?.amount}</div>
                        <div className="copy-icon pl-3 cursor-pointer text-gray-800">
                          <HiOutlineDuplicate size={25} onClick={(e: any) => handleCopyToClipboard(e, item, 'amount')} />
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell className="py-[10px]">
                      <div className="flex flex-row items-center">
                        <div className="text-black">{item?.reasonForDiscrepancy?.name}</div>
                        <div className="copy-icon pl-3 cursor-pointer text-gray-800">
                          <HiOutlineDuplicate size={25} onClick={(e: any) => handleCopyToClipboard(e, item, 'reasonforDiscrepancy')} />
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell className="py-[10px]">
                      <div className="flex flex-row items-center">
                        <div className="text-black">{item?.discreprancyNotes}</div>
                        <div className="copy-icon pl-3 cursor-pointer text-gray-800">
                          <HiOutlineDuplicate size={25} onClick={(e: any) => handleCopyToClipboard(e, item, 'discreprancyNotes')} />
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell className="py-[10px]">
                      <div className="flex flex-row items-center">
                        <div className="text-black">{`${item?.user?.firstName} ${item?.user?.lastName}`}</div>
                        <div className="copy-icon pl-3 cursor-pointer text-gray-800">
                          <HiOutlineDuplicate size={25} onClick={(e: any) => handleCopyToClipboard(e, item, 'user')} />
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell className="py-[10px]">
                      <div className="flex flex-row items-center">
                        <div className="text-black">{formatDateNotTime(item?.modified?.toString() || '')}</div>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
              {isLoading && <SpinnerComponent/>}
              <PaginateTable
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                totalEntities={totalEntities}
                isLoadingTable={isLoading}
              />
            </Table>
          </div>
        </>
      )}
    </div>
  );
};

export default StockLedger;
